import { EventChannel, SagaIterator } from 'redux-saga'
import { put, takeLatest, select, call, fork, all, takeEvery, delay } from 'redux-saga/effects'

import { isMobileOnly } from 'react-device-detect'
import JitsiMeetJS, { JitsiLocalTrack, JitsiMediaDevices } from 'lib-jitsi-meet'

import * as gridViewActions from '@tabeeb/modules/gridView/actions'
import * as gridViewSelectors from '@tabeeb/modules/gridView/selectors'
import { appConfigStateSelectors } from '@tabeeb/modules/appConfigState'
import {
  hideCallPreview,
  hideUserList,
  showCallPreview,
  showSelfView,
  showUserList,
} from '@tabeeb/modules/mobileWhiteboard/actions'
import { getShowUserList } from '@tabeeb/modules/mobileWhiteboard/selectors'
import { signalrActions } from '@tabeeb/modules/signalr'
import { mobileZoomMin, ZOOM_MULTIPLIER } from '@tabeeb/modules/../users/common/mobileZoom'
import { getContentId, getIsCurrentUserPresenter } from '@tabeeb/modules/shared/content/selectors'
import * as recordingActions from '@tabeeb/modules/recording/actions'
import { LocalRecordingOwnerActionType, RecordingType } from '@tabeeb/enums'
import { closeSwitchRecordingSourceDialog } from '@tabeeb/modules/whiteboard/actions'
import { onJitsiInitialized, openCallPreviewDialog } from '@tabeeb/modules/presentation/actions/conference'
import * as contentStateSelectors from '@tabeeb/shared/content/selectors'
import * as devicesActions from '@tabeeb/modules/presentation/actions/devices'
import * as tracksActions from '@tabeeb/modules/presentation/actions/tracks'
import * as jitsiTrackActions from '@tabeeb/modules/presentation/actions/jitsiTrack'
import * as connectionActions from '@tabeeb/modules/presentation/actions/connection'
import * as presentationSelectors from '@tabeeb/modules/presentation/selectors'
import * as trackService from '@tabeeb/modules/presentation/services/trackService'
import * as connectionService from '@tabeeb/modules/presentation/services/connectionService'
import * as conferenceService from '@tabeeb/modules/presentation/services/conferenceService'
import bindEvent from '@tabeeb/modules/presentation/services/events'
import * as usersActions from '@tabeeb/modules/../users/actions'
import * as usersSelectors from '@tabeeb/modules/../users/selectors'
import * as muteAudioActions from '@tabeeb/modules/presentationToolbar/actions/muteAudio'
import * as muteVideoActions from '@tabeeb/modules/presentationToolbar/actions/muteVideo'
import { notificationActions } from '@tabeeb/modules/notification'
import * as accountSelectors from '@tabeeb/modules/account/selectors'
import { setVideoMuted } from '@tabeeb/modules/presentation/actions/jitsiTrack'
import { onVideoMuted } from '@tabeeb/modules/presentationToolbar/actions/muteVideo'
import { RootState } from '@tabeeb/store'
import { VideoCallUser } from '@tabeeb/modules/../users/types'

import { getIsMuteAudioInProgress, getIsMuteVideoInProgress } from '@tabeeb/modules/presentationToolbar/selectors'
import {
  AUDIO_DEVICE,
  VIDEO_DEVICE,
  VIDEO_INPUT_KIND,
  DEFAULT_AUDIO_DEVICE_ID,
  CAMERA_FACING_MODE,
  AUDIO_INPUT_DEVICE_KEY,
  VIDEO_INPUT_DEVICE_KEY,
  AUDIO_OUTPUT_DEVICE_KEY,
  DEFAULT_VIDEO_DEVICE_ID,
  VIDEO_MUTED,
  AUDIO_MUTED,
  TabeebConnectEvents,
} from '../constants'

let externalEventChannels: EventChannel<unknown>[] = []

function* attachTrack({ payload: { track, userId } }: ReturnType<typeof tracksActions.attachTrack>): SagaIterator {
  if (track.getType() === VIDEO_DEVICE) {
    yield put(jitsiTrackActions.attachVideo(userId))
  } else {
    yield put(
      jitsiTrackActions.attachAudio({
        userId,
        container: trackService.getAudioContainerById(userId),
        trackToAttach: track,
      })
    )
  }
}

function* createLocalTracks(): SagaIterator {
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  let constraints: Awaited<ReturnType<typeof conferenceService.checkForDevices>> = yield call(
    conferenceService.checkForDevices
  )
  constraints = constraints.filter((item) => item !== '')

  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  if (constraints.length < 2) {
    yield put(
      notificationActions.onAddErrorNotification({
        message: 'No camera or mic is detected. To make a video call please plug a webcam or mic into your device',
        options: {
          autoHideDuration: 10000,
        },
      })
    )

    yield put(usersActions.resetVideoLoadForUser(currentUserId))

    jitsiRoom?.sendCommandOnce(TabeebConnectEvents.TRACKS_INITIALIZATION_FAILED, { value: currentUserId })
  }

  if (isMobileOnly) {
    yield put(connectionActions.onUserReadyForCall())
  } else {
    yield put(openCallPreviewDialog())
  }

  if (constraints.length === 0) {
    return
  }

  const isDeviceSwitchingEnabled = yield select(appConfigStateSelectors.getIsDeviceSwitchingEnabled)

  const audioInputDeviceId: Awaited<ReturnType<typeof conferenceService.getAudioInputDeviceId>> = yield call(
    conferenceService.getAudioInputDeviceId,
    isDeviceSwitchingEnabled
  )
  const audioOutputDeviceId: Awaited<ReturnType<typeof conferenceService.getAudioOutputDeviceId>> = yield call(
    conferenceService.getAudioOutputDeviceId,
    isDeviceSwitchingEnabled
  )
  const videoInputDeviceId: Awaited<ReturnType<typeof conferenceService.getVideoInputDeviceId>> = yield call(
    conferenceService.getVideoInputDeviceId
  )

  try {
    const tracks: Awaited<ReturnType<typeof connectionService.createLocalTracks>> = yield call(
      connectionService.createLocalTracks,
      {
        devices: constraints,
        resolution: `${window.config?.resolution ?? 720}`,
        micDeviceId: audioInputDeviceId,
        cameraDeviceId: videoInputDeviceId,
      }
    )
    yield put(tracksActions.setLocalTracks(tracks))

    for (let i = 0; i < tracks.length; i++) {
      yield put(jitsiTrackActions.addTrackToRoom(tracks[i]))
    }

    localStorage.setItem(VIDEO_INPUT_DEVICE_KEY, videoInputDeviceId)
    localStorage.setItem(AUDIO_INPUT_DEVICE_KEY, audioInputDeviceId)
    yield put(devicesActions.setAudioOutputDevice(audioOutputDeviceId || 'default'))
  } catch {
    yield put(
      notificationActions.onAddErrorNotification({
        message: 'No camera or mic is detected. To make a video call please plug a webcam or mic into your device',
        options: {
          autoHideDuration: 10000,
        },
      })
    )

    yield put(usersActions.resetVideoLoadForUser(currentUserId))

    jitsiRoom?.sendCommandOnce(TabeebConnectEvents.TRACKS_INITIALIZATION_FAILED, { value: currentUserId })
  } finally {
    yield put(onJitsiInitialized())
  }
}

function changeTrackMuteState(track: JitsiLocalTrack): Promise<void> {
  return new Promise((resolve) => {
    const promise = track.isMuted() ? track.unmute() : track.mute()
    promise.then(() => {
      resolve()
    })
  })
}

function* muteAudio(): SagaIterator {
  const tracks: ReturnType<typeof presentationSelectors.getConferenceTracksState> = yield select(
    presentationSelectors.getConferenceTracksState
  )
  const track = tracks.localTracks.find((t) => t.getType() === AUDIO_DEVICE)
  if (!track) {
    return
  }

  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  yield call(changeTrackMuteState, track)

  yield put(jitsiTrackActions.setAudioMuted())
  yield put(muteAudioActions.onAudioMuted())
  yield put(muteAudioActions.updateRemoteAudioIndicator(currentUserId))
}

function* muteVideo(): SagaIterator {
  const tracks: ReturnType<typeof presentationSelectors.getConferenceTracksState> = yield select(
    presentationSelectors.getConferenceTracksState
  )

  const track = tracks.localTracks.find((t) => t.getType() === VIDEO_DEVICE)
  if (track) {
    yield call(changeTrackMuteState, track)
    yield put(jitsiTrackActions.setVideoMuted())
    yield put(muteVideoActions.onVideoMuted())
  }
}

function* stopTracks(): SagaIterator {
  const localTracks: ReturnType<typeof presentationSelectors.getLocalTracks> = yield select(
    presentationSelectors.getLocalTracks
  )

  for (let i = 0; i < localTracks.length; i++) {
    yield put(jitsiTrackActions.stopTrack(localTracks[i]))
  }

  yield put(tracksActions.resetLocalTracks())
}

function* toggleLargeVideo(action: ReturnType<typeof tracksActions.toggleLargeVideo>): SagaIterator {
  const { userId } = action.payload
  const largeVideoUser: ReturnType<typeof presentationSelectors.getLargeVideoUser> = yield select(
    presentationSelectors.getLargeVideoUser
  )
  const showVideo = largeVideoUser?.id !== userId

  const isGridView: ReturnType<typeof gridViewSelectors.getIsGridViewActive> = yield select(
    gridViewSelectors.getIsGridViewActive
  )
  if (isGridView) {
    yield put(tracksActions.toggleGridView(false))
    yield put(gridViewActions.disableGridView())
  }

  if (trackService.isUnavailableForLargeVideo(largeVideoUser, showVideo, userId)) {
    return
  }

  if (largeVideoUser && largeVideoUser.id !== userId) {
    yield put(tracksActions.setLargeVideoUser(null))
    yield put(tracksActions.attachLargeVideo({ userId: largeVideoUser.id, isLargeVideo: false }))
  }

  const users: VideoCallUser[] = yield select((state: RootState) => state.users.users)
  const user = showVideo ? users.find((item) => item.id === userId) ?? null : null

  yield put(tracksActions.setLargeVideoUser(user))
  yield put(tracksActions.attachLargeVideo({ userId, isLargeVideo: showVideo }))

  /*
   * Elects the participant with the given id to be the selected participant in
   * order to receive higher video quality (if simulcast is enabled).
   */

  if (!showVideo) {
    // Check if video is large. It doesn't check if user has video
    return
  }

  try {
    const state: ReturnType<typeof presentationSelectors.getConferenceTracksState> = yield select(
      presentationSelectors.getConferenceTracksState
    )
    const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
      accountSelectors.getCurrentUserId
    )
    const track = trackService.getTrack(state, userId, currentUserId, VIDEO_DEVICE)
    if (!track) {
      // Check if user has video track
      return
    }
    const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
      presentationSelectors.getJitsiConference
    )

    const source = track.getSourceName()!

    jitsiRoom?.setReceiverConstraints({
      selectedSources: [source],
      onStageSources: [source],
      constraints: {
        [source]: { maxHeight: window.config?.constraints?.video?.height?.ideal ?? 720 },
      },
    })
  } catch (error) {
    console.error('Failed to select participant', error)
  }
}

function* attachLargeVideo(action: ReturnType<typeof tracksActions.attachLargeVideo>): SagaIterator {
  const { userId, isLargeVideo } = action.payload

  const $container = isLargeVideo ? trackService.getLargeVideoContainer() : trackService.getVideoContainerById(userId)
  if (!$container) {
    return
  }

  const state: ReturnType<typeof presentationSelectors.getConferenceTracksState> = yield select(
    presentationSelectors.getConferenceTracksState
  )
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  const track = trackService.getTrack(state, userId, currentUserId, VIDEO_DEVICE)
  if (!track) {
    return
  }

  yield put(jitsiTrackActions.detachTrack(track))
  track.attach($container)
}

function* toggleGridView(action: ReturnType<typeof tracksActions.toggleGridView>): SagaIterator {
  const isGridView = action.payload
  const state: ReturnType<typeof presentationSelectors.getConferenceTracksState> = yield select(
    presentationSelectors.getConferenceTracksState
  )
  const users: ReturnType<typeof usersSelectors.getUsersListForCurrentUser> = yield select(
    usersSelectors.getUsersListForCurrentUser
  )
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  if (isGridView) {
    yield put(tracksActions.setGridUser(users.find((item) => item.id === currentUserId)))
  } else {
    yield put(tracksActions.setGridUser(null))
  }

  const largeVideoUserId = state.largeVideoUser?.id
  if (isGridView && largeVideoUserId) {
    yield put(tracksActions.attachLargeVideo({ userId: largeVideoUserId, isLargeVideo: false }))
    yield put(tracksActions.setLargeVideoUser(null))
  }

  users.forEach((user) => {
    let $container
    if (isGridView) {
      $container = trackService.getGridVideoContainerById(user.id)
    } else if (!isGridView && largeVideoUserId === user.id) {
      $container = trackService.getLargeVideoContainer()
    } else {
      $container = trackService.getVideoContainerById(user.id)
    }

    if (!$container) {
      return
    }

    const track = trackService.getTrack(state, user.id, currentUserId, VIDEO_DEVICE)
    if (!track) {
      return
    }
    const { containers } = track
    if (containers.length > 0) {
      track.detach(containers[0])
    }
    track.attach($container)
  })
}

function* removeLocalVideoTrack(): SagaIterator {
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )
  const localTracks: ReturnType<typeof presentationSelectors.getLocalTracks> = yield select(
    presentationSelectors.getLocalTracks
  )
  const oldLocalVideoTrack = localTracks.find((item) => item.getType() === VIDEO_DEVICE)
  if (oldLocalVideoTrack) {
    yield call(muteVideo)

    // jitsiRoom is not initialized during the pre-call device test
    if (jitsiRoom) {
      yield call(jitsiRoom.removeTrack.bind(jitsiRoom), oldLocalVideoTrack)
    }
    yield put(tracksActions.removeLocalTrack(oldLocalVideoTrack))
    oldLocalVideoTrack.dispose()
  }
}

function* replaceTrack({
  payload: { track: newTrack, approvedByLocalRecording },
}: ReturnType<typeof tracksActions.replaceTrack>): SagaIterator {
  const newTrackType = newTrack.getType()
  const tracksState: ReturnType<typeof presentationSelectors.getConferenceTracksState> = yield select(
    presentationSelectors.getConferenceTracksState
  )
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )
  const localTrack = tracksState.localTracks.find((item) => item.getType() === newTrackType)
  const isCallPreview: ReturnType<typeof presentationSelectors.getIsCallPreviewOpen> = yield select(
    presentationSelectors.getIsCallPreviewOpen
  )

  const isVideoMuteInProgress: ReturnType<typeof getIsMuteVideoInProgress> = yield select(getIsMuteVideoInProgress)
  if (isVideoMuteInProgress && newTrackType === VIDEO_DEVICE && !localTrack) {
    yield put(muteVideoActions.onVideoMuted())
  }

  const isAudioMuteInProgress: ReturnType<typeof getIsMuteAudioInProgress> = yield select(getIsMuteAudioInProgress)
  if (isAudioMuteInProgress && newTrackType === AUDIO_DEVICE && !localTrack) {
    yield put(muteAudioActions.onAudioMuted())
  }

  if (!jitsiRoom && !isCallPreview) {
    newTrack.dispose()
    return
  }

  if (jitsiRoom) {
    try {
      yield delay(500)

      if (localTrack?.getVideoType() === newTrack.getVideoType()) {
        yield call(jitsiRoom.replaceTrack.bind(jitsiRoom), localTrack, newTrack)
      } else {
        if (localTrack) {
          yield call(jitsiRoom.removeTrack.bind(jitsiRoom), localTrack)
        }

        yield call(jitsiRoom.addTrack.bind(jitsiRoom), newTrack)
      }
    } catch {
      yield put(
        notificationActions.onAddErrorNotification({
          message: `Something wrong with your device.`,
        })
      )
    }
  }

  if (localTrack) {
    localTrack.dispose()
  }

  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  const userId: number = isCallPreview
    ? currentUserId
    : yield select((state) => presentationSelectors.getUserIdByParticipantId(state, newTrack.getParticipantId()))

  yield put(tracksActions.updateLocalTrack(newTrack))

  if (newTrackType === VIDEO_DEVICE) {
    yield put(jitsiTrackActions.attachVideo(userId))

    if (approvedByLocalRecording) {
      yield put(closeSwitchRecordingSourceDialog())

      const isCurrentUserPresenter = yield select(getIsCurrentUserPresenter)
      if (isCurrentUserPresenter) {
        const selectedUserId: number = yield select(accountSelectors.getCurrentUserId)
        // @ts-ignore
        yield put(recordingActions.onRecordingStarting({ userId: selectedUserId, recordingType: RecordingType.local }))
      } else {
        const contentId: ReturnType<typeof contentStateSelectors.getContentId> = yield select(
          contentStateSelectors.getContentId
        )
        yield put(
          signalrActions.invokeHubAction({
            method: 'LocalRecordingOwnerControlRecording',
            args: [contentId, currentUserId, LocalRecordingOwnerActionType.restart],
          })
        )
      }
    }
  }
}

function* bindTrackEvents(action: ReturnType<typeof tracksActions.bindTrackEvents>): SagaIterator {
  const track = action.payload
  const jitsiTrackEvents = JitsiMeetJS.events.track
  yield fork(
    bindEvent,
    track,
    jitsiTrackEvents.TRACK_MUTE_CHANGED,
    tracksActions.onTrackMuteChanged,
    externalEventChannels
  )
}

function unbindTrackEvents() {
  externalEventChannels.forEach((channel) => channel.close())
  externalEventChannels = []
}

function* onTrackMuteChanged(action: ReturnType<typeof tracksActions.onTrackMuteChanged>): SagaIterator {
  const track = action.payload

  const userId: ReturnType<typeof presentationSelectors.getUserIdByParticipantId> = yield select((state) =>
    presentationSelectors.getUserIdByParticipantId(state, track.getParticipantId())
  )

  yield put(usersActions.onUserTracksStateChanged(userId))
}

function* detachTracksForUser(action: ReturnType<typeof tracksActions.detachTracksForUser>): SagaIterator {
  const userId = action.payload
  const tracks: ReturnType<typeof presentationSelectors.getUserTracks> = yield select((state) =>
    presentationSelectors.getUserTracks(state, { Id: userId })
  )
  for (let i = 0; i < tracks.length; i++) {
    yield put(jitsiTrackActions.detachTrack(tracks[i]))
  }
}

function* setAudioOutputDevice(action: ReturnType<typeof devicesActions.setAudioOutputDevice>): SagaIterator {
  const deviceId = action.payload

  try {
    ;(JitsiMeetJS.mediaDevices as JitsiMediaDevices).setAudioOutputDevice(deviceId)
    localStorage.setItem(AUDIO_OUTPUT_DEVICE_KEY, deviceId)
  } catch {
    yield call(putDeviceErrorNotification, deviceId)
  }
}

function* setAudioInputDevice(action: ReturnType<typeof devicesActions.setAudioInputDevice>): SagaIterator {
  const audioInputDeviceId = action.payload

  try {
    const newTracks: Awaited<ReturnType<typeof connectionService.createLocalTracks>> = yield call(
      connectionService.createLocalTracks,
      {
        devices: [AUDIO_DEVICE],
        micDeviceId: audioInputDeviceId,
      }
    )
    const audioTrack = newTracks.find((track) => track.getType() === AUDIO_DEVICE)
    if (!audioTrack) {
      throw new Error('No audio track')
    }

    yield put(tracksActions.replaceTrack({ track: audioTrack }))
    localStorage.setItem(AUDIO_INPUT_DEVICE_KEY, audioInputDeviceId)
  } catch {
    yield call(putDeviceErrorNotification, audioInputDeviceId)
  }
}

function* setVideoInputDevice(action: ReturnType<typeof devicesActions.setVideoInputDevice>): SagaIterator {
  const isScreenSharing: ReturnType<typeof presentationSelectors.getIsScreenSharing> = yield select(
    presentationSelectors.getIsScreenSharing
  )
  const isScreenSharingInProgress: ReturnType<typeof presentationSelectors.getIsScreenSharingInProgress> = yield select(
    presentationSelectors.getIsScreenSharingInProgress
  )

  if (isScreenSharing || isScreenSharingInProgress) {
    return
  }

  const { deviceId, approvedByLocalRecording, muted = false } = action.payload

  const initTracks = {
    devices: [VIDEO_DEVICE],
    cameraDeviceId: deviceId,
  }

  try {
    const newTracks: Awaited<ReturnType<typeof connectionService.createLocalTracks>> = yield call(
      connectionService.createLocalTracks,
      initTracks
    )

    const videoTrack = newTracks.find((track) => track.getType() === VIDEO_DEVICE)
    if (!videoTrack) {
      throw new Error('No video track')
    }

    if (muted) {
      yield call([videoTrack, videoTrack.mute])
      yield put(setVideoMuted())
      yield put(onVideoMuted())
    }
    yield put(tracksActions.replaceTrack({ track: videoTrack, approvedByLocalRecording }))
    localStorage.setItem(VIDEO_INPUT_DEVICE_KEY, deviceId)
  } catch {
    yield call(removeLocalVideoTrack)
    yield call(putDeviceErrorNotification, deviceId)
  }
}

function* loadCameraPreviews(action: ReturnType<typeof devicesActions.loadCameraPreviews>): SagaIterator {
  const videoDevices = yield call(conferenceService.getDevices, VIDEO_INPUT_KIND)
  yield call(unloadCameraPreviews)
  for (let i = 0; i < videoDevices.length; i++) {
    const device = videoDevices[i]

    const initTracks = {
      devices: [VIDEO_DEVICE],
      cameraDeviceId: device.deviceId,
    }
    try {
      const tracks: Awaited<ReturnType<typeof connectionService.createLocalTracks>> = yield call(
        connectionService.createLocalTracks,
        initTracks
      )
      const container = yield call(trackService.getPreviewVideoContainerById, device.deviceId)

      const videoTrack = tracks.find((track) => track.getType() === VIDEO_DEVICE)
      if (videoTrack) {
        if (container == null) {
          videoTrack.dispose()
        } else {
          yield put(tracksActions.addPreviewTrack(videoTrack))
          videoTrack.attach(container)
        }
      }
    } catch {
      yield call(putDeviceErrorNotification, device.deviceId)
    }
  }
}

function* unloadCameraPreviews(): SagaIterator {
  const previewTracks: ReturnType<typeof presentationSelectors.getPreviewTracks> = yield select(
    presentationSelectors.getPreviewTracks
  )

  previewTracks.forEach((track) => {
    track.dispose()
  })

  yield put(tracksActions.resetPreviewTracks())
}

function* putDeviceErrorNotification(deviceId: string): SagaIterator {
  const device: Awaited<ReturnType<typeof conferenceService.getDeviceById>> = yield call(
    conferenceService.getDeviceById,
    deviceId
  )

  const deviceLabel = device?.label
  if (!deviceLabel) {
    return
  }

  yield put(
    notificationActions.onAddErrorNotification({
      message: `${deviceLabel} is not available.`,
    })
  )
}

function* flipMobileCamera(): SagaIterator {
  const localTracks: ReturnType<typeof presentationSelectors.getLocalTracks> = yield select(
    presentationSelectors.getLocalTracks
  )

  const localTrack = localTracks.find((item) => item.getType() === VIDEO_DEVICE)
  if (!localTrack || localTrack.isMuted()) {
    return
  }

  yield call(muteVideo)

  const currentFacingMode = localTrack.getCameraFacingMode()

  const targetFacingMode =
    currentFacingMode === CAMERA_FACING_MODE.USER ? CAMERA_FACING_MODE.ENVIRONMENT : CAMERA_FACING_MODE.USER

  const initTracks = {
    devices: [VIDEO_DEVICE],
    facingMode: targetFacingMode,
  }

  yield delay(500)
  try {
    const tracks: Awaited<ReturnType<typeof connectionService.createLocalTracks>> = yield call(
      connectionService.createLocalTracks,
      initTracks
    )
    const videoTrack = tracks.find((track) => track.getType() === VIDEO_DEVICE)
    if (!videoTrack) {
      throw new Error('No video track')
    }

    yield put(tracksActions.replaceTrack({ track: videoTrack }))

    const capabilities = videoTrack.track.getCapabilities()
    const hasZoom = Boolean(capabilities.zoom)

    const user: ReturnType<typeof usersSelectors.getCurrentUser> = yield select(usersSelectors.getCurrentUser)
    const minZoom = parseInt(mobileZoomMin, 10)
    const maxZoom = hasZoom ? capabilities.zoom.max * ZOOM_MULTIPLIER : minZoom

    if (user.mobileZoomValue > minZoom) {
      const contentId: ReturnType<typeof getContentId> = yield select(getContentId)
      yield put(
        signalrActions.invokeHubAction({
          method: 'UpdateZoomingValue',
          args: [contentId, user.id, minZoom, maxZoom],
        })
      )
    }

    yield put(devicesActions.setMobileCameraFacingMode(targetFacingMode))
  } catch {
    yield put(
      notificationActions.onAddErrorNotification({
        message: `Something wrong with your device.`,
      })
    )
  }
}

function* onShowCallPreview(): SagaIterator {
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  let constraints: Awaited<ReturnType<typeof conferenceService.checkForDevices>> = yield call(
    conferenceService.checkForDevices
  )
  constraints = constraints.filter((item) => item !== '')

  if (constraints.length === 0) {
    return
  }

  const audioInputDeviceId = DEFAULT_AUDIO_DEVICE_ID
  const videoDevices = yield call(conferenceService.getVideoInputDevices)
  const videoInputDeviceId = videoDevices[0] ? videoDevices[0].deviceId : DEFAULT_VIDEO_DEVICE_ID

  const initTracks = {
    devices: constraints,
    micDeviceId: audioInputDeviceId,
    cameraDeviceId: videoInputDeviceId,
  }
  try {
    yield call(connectionService.initJitsi)

    const tracks: Awaited<ReturnType<typeof connectionService.createLocalTracks>> = yield call(
      connectionService.createLocalTracks,
      initTracks
    )
    yield put(tracksActions.setLocalTracks(tracks))

    const videoContainer = yield call(trackService.getVideoContainerById, currentUserId)
    const videoTrack = tracks.find((track) => track.getType() === VIDEO_DEVICE)
    if (videoTrack) {
      if (videoContainer == null) {
        videoTrack.dispose()
      } else {
        yield put(devicesActions.setVideoInputDevice({ deviceId: videoInputDeviceId }))
        videoTrack.attach(videoContainer)
      }
    }
  } catch {
    yield put(usersActions.resetVideoLoadForUser(currentUserId))
    yield put(
      notificationActions.onAddErrorNotification({
        message: 'No camera or mic is detected. To make a video call please plug a webcam or mic into your device',
        options: {
          autoHideDuration: 10000,
        },
      })
    )
  }
}

function* onHideCallPreview(): SagaIterator {
  const localTracks: ReturnType<typeof presentationSelectors.getLocalTracks> = yield select(
    presentationSelectors.getLocalTracks
  )

  localTracks.forEach((track) => {
    track.dispose()
  })

  yield put(tracksActions.resetLocalTracks())
}

function* toggleMobileLargeVideo(action: ReturnType<typeof tracksActions.toggleMobileLargeVideo>): SagaIterator {
  if (!isMobileOnly) {
    return
  }

  const isPriorityView = yield select(presentationSelectors.getIsPriorityViewEnabled)
  const isGridView = yield select(gridViewSelectors.getIsGridViewActive)
  const isUserList = yield select(getShowUserList)
  const largeVideoUser = yield select(presentationSelectors.getLargeVideoUser)
  const currentUserId = yield select(accountSelectors.getCurrentUserId)

  // turn off
  if (!isPriorityView && !isGridView && !isUserList) {
    if (largeVideoUser.id === currentUserId) {
      yield put(tracksActions.setLargeVideoUser(null))
      yield put(tracksActions.attachLargeVideo({ userId: largeVideoUser.id, isLargeVideo: false }))
    }

    yield put(tracksActions.toggleMobilePriorityView(true))
    return
  }

  // turn on from priority view

  if (isPriorityView) {
    yield put(tracksActions.toggleMobilePriorityView(false))
  }

  // turn on from grid view

  if (isGridView) {
    yield put(gridViewActions.disableGridView())
  }

  // turn on from user list

  if (isUserList) {
    yield put(hideUserList({ turnOnPriorityMode: false }))
  }

  if (largeVideoUser && largeVideoUser.id === action.payload.userId) {
    return
  }

  yield put(tracksActions.toggleLargeVideo(action.payload))
}

function* onShowMobileContactList(): SagaIterator {
  const isPriorityView: ReturnType<typeof presentationSelectors.getIsPriorityViewEnabled> = yield select(
    presentationSelectors.getIsPriorityViewEnabled
  )
  const largeVideoUser: ReturnType<typeof presentationSelectors.getLargeVideoUser> = yield select(
    presentationSelectors.getLargeVideoUser
  )

  if (isPriorityView) {
    yield put(tracksActions.toggleMobilePriorityView(false))
  }

  if (largeVideoUser) {
    yield put(tracksActions.setLargeVideoUser(null))
    yield put(tracksActions.attachLargeVideo({ userId: largeVideoUser.id, isLargeVideo: false }))
  }
}

function* onHideMobileContactList({ payload }: ReturnType<typeof hideUserList>): SagaIterator {
  const { turnOnPriorityMode } = payload

  if (turnOnPriorityMode) {
    yield put(tracksActions.toggleMobilePriorityView(true))
    yield put(showSelfView())
  }
}

function* toggleMobileUserList({ payload }: ReturnType<typeof tracksActions.toggleMobileUserList>): SagaIterator {
  const isUserList = payload

  const state: ReturnType<typeof presentationSelectors.getConferenceTracksState> = yield select(
    presentationSelectors.getConferenceTracksState
  )
  const users: ReturnType<typeof usersSelectors.getUsersListForCurrentUser> = yield select(
    usersSelectors.getUsersListForCurrentUser
  )
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  const isPriorityView: ReturnType<typeof presentationSelectors.getIsPriorityViewEnabled> = yield select(
    presentationSelectors.getIsPriorityViewEnabled
  )
  const largeVideoUser: ReturnType<typeof presentationSelectors.getLargeVideoUser> = yield select(
    presentationSelectors.getLargeVideoUser
  )

  if (isUserList && isPriorityView) {
    yield put(tracksActions.toggleMobilePriorityView(false))
  }

  if (isUserList && largeVideoUser) {
    yield put(tracksActions.setLargeVideoUser(null))
    yield put(tracksActions.attachLargeVideo({ userId: largeVideoUser.id, isLargeVideo: false }))
  }

  users.forEach((user) => {
    let $container
    if (largeVideoUser?.id === user.id) $container = trackService.getLargeVideoContainer()
    else $container = trackService.getVideoContainerById(user.id)

    if (!$container) {
      return
    }

    const track = trackService.getTrack(state, user.id, currentUserId, VIDEO_DEVICE)
    if (!track) {
      return
    }

    const { containers } = track
    if (containers.length > 0) {
      track.detach(containers[0])
    }
    track.attach($container)
  })
}

function* toggleMobileFlash({ payload }: ReturnType<typeof tracksActions.toggleMobileFlash>): SagaIterator {
  const localTracks: ReturnType<typeof presentationSelectors.getLocalTracks> = yield select(
    presentationSelectors.getLocalTracks
  )

  const localTrack = localTracks.find((item) => item.getType() === VIDEO_DEVICE)
  if (!localTrack) {
    return
  }

  const capabilities = localTrack.track.getCapabilities()
  if (!capabilities.torch) {
    return
  }

  yield localTrack.track.applyConstraints({
    advanced: [{ torch: payload }],
  })
}

function* updateMobileZoom({ payload }: ReturnType<typeof tracksActions.updateMobileZoom>): SagaIterator {
  const { userId, contentId, zoomValue } = payload

  const localTracks: ReturnType<typeof presentationSelectors.getLocalTracks> = yield select(
    presentationSelectors.getLocalTracks
  )

  try {
    const localTrack = localTracks.find((item) => item.getType() === VIDEO_DEVICE)
    if (!localTrack) {
      throw new Error('No video track')
    }

    const capabilities = localTrack.track.getCapabilities()
    if (!capabilities.zoom || zoomValue > capabilities.zoom.max) {
      return
    }

    yield put(
      signalrActions.invokeHubAction({
        method: 'UpdateZoomingValue',
        args: [contentId, userId, zoomValue * ZOOM_MULTIPLIER, capabilities.zoom.max * ZOOM_MULTIPLIER],
      })
    )

    yield localTrack.track.applyConstraints({
      advanced: [{ zoom: zoomValue }],
    })
  } catch {
    yield put(
      notificationActions.onAddErrorNotification({
        message: 'An error occurred while zooming.',
        options: {
          autoHideDuration: 10000,
        },
      })
    )
  }
}

function* handleSettingMutedDeviceState({
  payload: newTrack,
}: ReturnType<typeof tracksActions.updateLocalTrack>): SagaIterator {
  const callPreviewOpen: ReturnType<typeof presentationSelectors.getIsCallPreviewOpen> = yield select(
    presentationSelectors.getIsCallPreviewOpen
  )
  if (!callPreviewOpen) {
    return
  }

  const newTrackType = newTrack.getType()
  if (newTrackType === AUDIO_DEVICE) {
    const parsedBooleanAudioMuted = JSON.parse(localStorage.getItem(AUDIO_MUTED) || 'false')
    if (parsedBooleanAudioMuted === true) {
      yield put(muteAudioActions.muteAudio())
    }
  } else if (newTrackType === VIDEO_DEVICE) {
    const parsedBooleanVideoMuted = JSON.parse(localStorage.getItem(VIDEO_MUTED) || 'false')
    if (parsedBooleanVideoMuted === true) {
      yield put(muteVideoActions.muteVideo())
    }
  }
}

function* tracksSaga() {
  yield all([
    takeLatest(tracksActions.createLocalTracks, createLocalTracks),
    takeLatest(tracksActions.attachTrack, attachTrack),
    takeLatest(muteAudioActions.muteAudio, muteAudio),
    takeLatest(muteVideoActions.muteVideo, muteVideo),
    takeLatest(tracksActions.stopTracks, stopTracks),
    takeLatest(tracksActions.toggleLargeVideo, toggleLargeVideo),
    takeLatest(tracksActions.attachLargeVideo, attachLargeVideo),
    takeEvery(tracksActions.replaceTrack, replaceTrack),
    takeLatest(tracksActions.removeLocalVideoTrack, removeLocalVideoTrack),
    takeLatest(tracksActions.bindTrackEvents, bindTrackEvents),
    takeLatest(tracksActions.unbindTrackEvents, unbindTrackEvents),
    takeLatest(tracksActions.onTrackMuteChanged, onTrackMuteChanged),
    takeLatest(tracksActions.detachTracksForUser, detachTracksForUser),
    takeEvery(devicesActions.setAudioOutputDevice, setAudioOutputDevice),
    takeEvery(devicesActions.setAudioInputDevice, setAudioInputDevice),
    takeEvery(devicesActions.setVideoInputDevice, setVideoInputDevice),
    takeEvery(devicesActions.loadCameraPreviews, loadCameraPreviews),
    takeEvery(devicesActions.unloadCameraPreviews, unloadCameraPreviews),
    takeLatest(devicesActions.flipMobileCamera, flipMobileCamera),
    takeLatest(tracksActions.toggleGridView, toggleGridView),
    takeLatest(showCallPreview, onShowCallPreview),
    takeLatest(hideCallPreview, onHideCallPreview),
    takeLatest(tracksActions.toggleMobileLargeVideo, toggleMobileLargeVideo),
    takeLatest(showUserList, onShowMobileContactList),
    takeLatest(hideUserList, onHideMobileContactList),
    takeLatest(tracksActions.toggleMobileUserList, toggleMobileUserList),
    takeLatest(tracksActions.toggleMobileFlash, toggleMobileFlash),
    takeLatest(tracksActions.updateMobileZoom, updateMobileZoom),
    takeEvery(tracksActions.updateLocalTrack, handleSettingMutedDeviceState),
  ])
}

export default tracksSaga
